import {
  addTrack,
  formatTimecode,
  secondsToFrame,
  sequenceDuration,
  type Track,
  type TrackKind,
} from '@ve/core';
import {
  ArrowLeftRight,
  Columns2,
  Magnet,
  MousePointer2,
  Plus,
  Repeat,
  Scissors,
  SplitSquareHorizontal,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { formatShortcut } from '../../lib/commands.ts';
import { commandTip, tip } from '../../lib/tooltip.ts';
import { edit, useEditor, type Tool } from '../../state/editor-store.ts';
import { usePlayback } from '../../state/playback.ts';
import { useUi } from '../../state/ui-store.ts';
import { beginMarquee } from './interactions.ts';
import {
  dropSources,
  dropLibraryItem,
  payloadKind,
  readPayload,
  resolveDropTarget,
  snapDropFrame,
  UNKNOWN_DURATION_SECONDS,
  type DropOver,
  type DropPayload,
  type DropTarget,
} from './drop.ts';
import { clearActiveDrag, DND_TRANSITION } from '../../lib/dnd.ts';
import { closeSourcePreview } from '../../state/source-preview.ts';
import { sourcesFromDrop } from '../../state/session.ts';
import { Playhead } from './Playhead.tsx';
import { Ruler } from './Ruler.tsx';
import { TrackHeader } from './TrackHeader.tsx';
import { TrackLane } from './TrackLane.tsx';

const HEADER_W = 172;
const VIDEO_H = 62;
const AUDIO_H = 52;
const TAIL_SECONDS = 30;

const TOOLS: Array<{ id: Tool; description: string; key: string; icon: typeof MousePointer2 }> = [
  { id: 'select', description: 'Selection — move clips, drag edges to trim', key: 'V', icon: MousePointer2 },
  { id: 'razor', description: 'Razor — click a clip to cut it', key: 'C', icon: Scissors },
  {
    id: 'ripple',
    description: 'Ripple edit — trimming shifts the following clips',
    key: 'B',
    icon: Columns2,
  },
  {
    id: 'roll',
    description: 'Rolling edit — move a cut between two clips, keeping the total length',
    key: 'N',
    icon: SplitSquareHorizontal,
  },
  {
    id: 'slip',
    description: 'Slip — change which part of the source a clip shows',
    key: 'Y',
    icon: ArrowLeftRight,
  },
];

export function Timeline() {
  const project = useEditor((s) => s.project)!;
  const seqId = useEditor((s) => s.activeSequenceId) ?? project.rootSequenceId;
  const sequence = project.sequences[seqId]!;
  const pxPerFrame = useEditor((s) => s.pxPerFrame);
  const selection = useEditor((s) => s.selection);
  const loopRange = useEditor((s) => s.loopRange);
  const loopEnabled = useEditor((s) => s.loopEnabled);
  const zoomToFitToken = useUi((s) => s.zoomToFitToken);
  const fps = project.settings.fps;

  const scrollRef = useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = useState({ left: 0, width: 800 });
  const [snapLine, setSnapLine] = useState<number | null>(null);
  const [marquee, setMarquee] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [drop, setDrop] = useState<DropPreview | null>(null);
  const snapping = useEditor((s) => s.snapping);

  // A drag that ends anywhere (Escape, drop elsewhere) must clear the preview.
  useEffect(() => {
    const clear = () => setDrop(null);
    window.addEventListener('dragend', clear);
    window.addEventListener('drop', clear);
    return () => {
      window.removeEventListener('dragend', clear);
      window.removeEventListener('drop', clear);
    };
  }, []);

  /** Works out payload, target track and geometry of a drop at the event's position. */
  const computeDrop = (e: React.DragEvent): DropPreview | null => {
    if (e.dataTransfer.types.includes(DND_TRANSITION)) return null; // handled by the lanes
    const payload = readPayload(e.dataTransfer, project);
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!payload || !el || !content) return null;
    const rect = el.getBoundingClientRect();
    const rawFrame = Math.max(0, Math.round((e.clientX - rect.left - HEADER_W + el.scrollLeft) / pxPerFrame));
    const frames =
      payload.type === 'files'
        ? secondsToFrame(UNKNOWN_DURATION_SECONDS, fps) * payload.count
        : payload.frames;
    const frame = snapping && !e.shiftKey ? snapDropFrame(sequence, rawFrame, frames, pxPerFrame) : rawFrame;
    const kind = payloadKind(payload);
    const target = resolveDropTarget(sequence, dropOverAt(e.clientX, e.clientY), kind, frame, frames);
    const row = content.querySelector<HTMLElement>(
      target.trackId ? `[data-track-id="${target.trackId}"]` : `[data-new-track="${kind}"]`,
    );
    const contentRect = content.getBoundingClientRect();
    const rowRect = row?.getBoundingClientRect();
    return {
      payload,
      target,
      frames,
      top: rowRect ? rowRect.top - contentRect.top : 26,
      height: rowRect?.height ?? (kind === 'video' ? VIDEO_H : AUDIO_H),
      insert: e.altKey,
    };
  };

  const onDragOver = (e: React.DragEvent) => {
    const next = computeDrop(e);
    if (!next) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDrop((prev) => (prev && samePreview(prev, next) ? prev : next));
  };

  const onDrop = (e: React.DragEvent) => {
    const result = computeDrop(e);
    setDrop(null);
    clearActiveDrag();
    if (!result) return;
    e.preventDefault();
    if (result.payload.type === 'files') {
      const sources = sourcesFromDrop(e.dataTransfer); // must run synchronously in the event
      if (sources) void dropSources(sources, result.target);
    } else dropLibraryItem(result.payload, result.target, e.altKey ? 'insert' : 'overwrite');
  };

  const duration = sequenceDuration(project, sequence);
  const contentFrames = duration + TAIL_SECONDS * Math.round(fps.num / fps.den);
  const lanesWidth = Math.max(scroll.width, contentFrames * pxPerFrame);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) setScroll({ left: el.scrollLeft, width: el.clientWidth - HEADER_W });
  }, []);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    onScroll();
    const ro = new ResizeObserver(onScroll);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onScroll]);

  // Zoom to fit on request and when switching sequences.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const visible = el.clientWidth - HEADER_W - 40;
    const frames = Math.max(
      sequenceDuration(useEditor.getState().project!, seqId),
      10 * Math.round(fps.num / fps.den),
    );
    useEditor.getState().setZoom(visible / frames);
    el.scrollLeft = 0;
  }, [zoomToFitToken, seqId, fps]);

  // Cmd/Ctrl + wheel zooms around the pointer.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const state = useEditor.getState();
      const rect = el.getBoundingClientRect();
      const px = e.clientX - rect.left - HEADER_W + el.scrollLeft;
      const frameAtPointer = px / state.pxPerFrame;
      state.setZoom(state.pxPerFrame * Math.exp(-e.deltaY * 0.004));
      const next = useEditor.getState().pxPerFrame;
      el.scrollLeft = Math.max(0, frameAtPointer * next - (e.clientX - rect.left - HEADER_W));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const viewport = { left: scroll.left, right: scroll.left + scroll.width };
  const videoTracks = [...sequence.videoTracks].reverse();
  const rows: Array<{ track: Track; height: number }> = [
    ...videoTracks.map((t) => ({ track: t, height: VIDEO_H })),
    ...sequence.audioTracks.map((t) => ({ track: t, height: AUDIO_H })),
  ];

  const pickClips = (rect: DOMRect) => {
    const ids: string[] = [];
    scrollRef.current?.querySelectorAll<HTMLElement>('[data-clip-id]').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.right >= rect.left && r.left <= rect.right && r.bottom >= rect.top && r.top <= rect.bottom) {
        ids.push(el.dataset.clipId!);
      }
    });
    return ids;
  };

  return (
    <section className="panel timeline" onPointerDownCapture={closeSourcePreview}>
      <TimelineToolbar sequenceName={sequence.name} durationFrames={duration} />
      <div
        className="timeline-scroll"
        ref={scrollRef}
        onScroll={onScroll}
        onDragOver={onDragOver}
        onDragLeave={(e) => {
          if (!scrollRef.current?.contains(e.relatedTarget as Node | null)) setDrop(null);
        }}
        onDrop={onDrop}
        onPointerDown={(e) => {
          const target = e.target as HTMLElement;
          if (e.button !== 0 || !target.classList.contains('lane')) return;
          beginMarquee(e, scrollRef.current!, setMarquee, pickClips, e.shiftKey || e.metaKey);
        }}
      >
        <div className="timeline-content" ref={contentRef} style={{ width: HEADER_W + lanesWidth }}>
          <div className="timeline-ruler-row">
            <div className="timeline-corner" style={{ width: HEADER_W }}>
              <TimecodeDisplay />
            </div>
            <Ruler
              pxPerFrame={pxPerFrame}
              fps={fps}
              width={lanesWidth}
              scrollLeft={scroll.left}
              viewportWidth={scroll.width}
              markers={sequence.markers}
              loopRange={loopRange}
              loopEnabled={loopEnabled}
            />
            {drop && payloadKind(drop.payload) === 'video' && <NewTrackZone kind="video" />}
          </div>
          {rows.map(({ track, height }, i) => (
            <div key={track.id} className={`timeline-row${i === videoTracks.length ? ' first-audio' : ''}`}>
              <div className="timeline-header-cell" style={{ width: HEADER_W }}>
                <TrackHeader track={track} height={height} />
              </div>
              <TrackLane
                track={track}
                sequence={sequence}
                pxPerFrame={pxPerFrame}
                fps={fps}
                height={height}
                width={lanesWidth}
                viewport={viewport}
                selectedIds={selection.clipIds}
                selectedTransitionId={selection.transitionId}
                onSnap={setSnapLine}
              />
            </div>
          ))}
          <div className="timeline-tail">
            {drop && payloadKind(drop.payload) === 'audio' && <NewTrackZone kind="audio" />}
            <div className="timeline-add-tracks" style={{ width: HEADER_W }}>
              <button
                className="btn ghost small"
                onClick={() => edit('Add video track', (p) => addTrack(p, seqId, 'video'))}
                {...tip('Add a video track on top')}
              >
                <Plus size={12} /> Video
              </button>
              <button
                className="btn ghost small"
                onClick={() => edit('Add audio track', (p) => addTrack(p, seqId, 'audio'))}
                {...tip('Add an audio track at the bottom')}
              >
                <Plus size={12} /> Audio
              </button>
            </div>
          </div>
          <Playhead pxPerFrame={pxPerFrame} offset={HEADER_W} scrollRef={scrollRef} headerWidth={HEADER_W} />
          {snapLine !== null && (
            <div className="snap-line" style={{ left: HEADER_W + snapLine * pxPerFrame }} />
          )}
          {drop && <DropGhost preview={drop} pxPerFrame={pxPerFrame} />}
          {marquee && (
            <div
              className="marquee"
              style={{
                left: Math.min(marquee.x0, marquee.x1),
                top: Math.min(marquee.y0, marquee.y1),
                width: Math.abs(marquee.x1 - marquee.x0),
                height: Math.abs(marquee.y1 - marquee.y0),
              }}
            />
          )}
        </div>
      </div>
    </section>
  );
}

interface DropPreview {
  payload: DropPayload;
  target: DropTarget;
  frames: number;
  top: number;
  height: number;
  insert: boolean;
}

function samePreview(a: DropPreview, b: DropPreview): boolean {
  return (
    a.target.trackId === b.target.trackId &&
    a.target.frame === b.target.frame &&
    a.top === b.top &&
    a.frames === b.frames &&
    a.insert === b.insert &&
    a.payload.label === b.payload.label
  );
}

/** Track (or new-track zone) under a viewport point. */
function dropOverAt(x: number, y: number): DropOver {
  for (const el of document.elementsFromPoint(x, y)) {
    const { trackId, newTrack } = (el as HTMLElement).dataset ?? {};
    if (trackId) return { trackId };
    if (newTrack === 'video' || newTrack === 'audio') return { newTrack };
  }
  return null;
}

/**
 * Drop zone for creating a new track, shown while dragging. It overlays existing chrome (the ruler
 * for video, the strip below the tracks for audio) so the track rows never shift mid-drag.
 */
function NewTrackZone({ kind }: { kind: TrackKind }) {
  return (
    <div className={`new-track-zone ${kind}`} data-new-track={kind}>
      <span className="new-track-label">
        <Plus size={12} /> Drop here for a new {kind} track
      </span>
    </div>
  );
}

/** Outline of where the dragged item will land. */
function DropGhost({ preview, pxPerFrame }: { preview: DropPreview; pxPerFrame: number }) {
  const { payload, target, frames, top, height, insert } = preview;
  const kind = payloadKind(payload);
  const files = payload.type === 'files';
  return (
    <div
      className={`drop-ghost ${kind}${files ? ' files' : ''}${insert ? ' insert' : ''}`}
      style={{
        left: HEADER_W + target.frame * pxPerFrame,
        top: top + 1,
        width: Math.max(4, frames * pxPerFrame),
        height: height - 2,
      }}
    >
      <span>
        {files ? `Upload ${payload.label}` : payload.label}
        {target.trackId === null && ' · new track'}
        {insert && ' · insert'}
      </span>
    </div>
  );
}

function TimecodeDisplay() {
  const frame = usePlayback((s) => s.frame);
  const fps = useEditor((s) => s.project!.settings.fps);
  return <span className="timecode">{formatTimecode(frame, fps)}</span>;
}

function TimelineToolbar({ sequenceName, durationFrames }: { sequenceName: string; durationFrames: number }) {
  const tool = useEditor((s) => s.tool);
  const setTool = useEditor((s) => s.setTool);
  const snapping = useEditor((s) => s.snapping);
  const setSnapping = useEditor((s) => s.setSnapping);
  const pxPerFrame = useEditor((s) => s.pxPerFrame);
  const setZoom = useEditor((s) => s.setZoom);
  const loopEnabled = useEditor((s) => s.loopEnabled);
  const setLoopEnabled = useEditor((s) => s.setLoopEnabled);
  const fps = useEditor((s) => s.project!.settings.fps);

  return (
    <div className="panel-header timeline-toolbar">
      <SequenceTabs />
      <span className="spacer" />
      <span className="muted timeline-duration" {...tip(`Duration of “${sequenceName}”`)}>
        {formatTimecode(durationFrames, fps)}
      </span>
      <div className="tool-group">
        {TOOLS.map((t) => (
          <button
            key={t.id}
            className={`btn ghost small icon${tool === t.id ? ' active' : ''}`}
            {...tip(t.description, t.key)}
            onClick={() => setTool(t.id)}
          >
            <t.icon size={14} />
          </button>
        ))}
      </div>
      <button
        className={`btn ghost small icon${snapping ? ' active' : ''}`}
        {...commandTip(
          'snapping',
          snapping
            ? 'Snapping on — clips snap to edits, markers and the playhead (hold Shift to bypass)'
            : 'Snapping off',
        )}
        onClick={() => setSnapping(!snapping)}
      >
        <Magnet size={14} />
      </button>
      <button
        className={`btn ghost small icon${loopEnabled ? ' active' : ''}`}
        {...commandTip(
          'loopToggle',
          loopEnabled ? 'Stop looping' : 'Loop playback between in (I) and out (O)',
        )}
        onClick={() => setLoopEnabled(!loopEnabled)}
      >
        <Repeat size={14} />
      </button>
      <div className="zoom-control">
        <button
          className="btn ghost small icon"
          {...commandTip('zoomOut')}
          onClick={() => setZoom(pxPerFrame / 1.5)}
        >
          <ZoomOut size={14} />
        </button>
        <input
          type="range"
          min={Math.log(0.02)}
          max={Math.log(40)}
          step={0.01}
          value={Math.log(pxPerFrame)}
          onChange={(e) => setZoom(Math.exp(Number(e.target.value)))}
          {...tip(`Timeline zoom (${formatShortcut('Mod')} + scroll, ${formatShortcut('Shift+Z')} to fit)`)}
        />
        <button
          className="btn ghost small icon"
          {...commandTip('zoomIn')}
          onClick={() => setZoom(pxPerFrame * 1.5)}
        >
          <ZoomIn size={14} />
        </button>
      </div>
    </div>
  );
}

function SequenceTabs() {
  const project = useEditor((s) => s.project)!;
  const open = useEditor((s) => s.openSequenceIds);
  const active = useEditor((s) => s.activeSequenceId);
  const openSequence = useEditor((s) => s.openSequence);
  const closeSequence = useEditor((s) => s.closeSequence);
  return (
    <div className="sequence-tabs">
      {open.map((id) => {
        const seq = project.sequences[id];
        if (!seq) return null;
        return (
          <div
            key={id}
            className={`sequence-tab${id === active ? ' active' : ''}`}
            onClick={() => openSequence(id)}
            {...tip(id === project.rootSequenceId ? 'Main timeline' : `Chapter “${seq.name}”`)}
          >
            {seq.name}
            {id !== project.rootSequenceId && (
              <button
                className="sequence-tab-close"
                {...tip(`Close tab (the chapter is kept)`)}
                onClick={(e) => {
                  e.stopPropagation();
                  closeSequence(id);
                }}
              >
                <X size={11} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
